import React, { useEffect, useState } from "react";
import {
  ClientNotification,
  ClientNotificationType,
} from "../../interfaces/clientNotification";
import {
  getNotificationTypes,
  markAllAsRead,
  markAsRead,
} from "../../services/notificationsService";
import { useNavigate } from "react-router-dom";
import { formatTime } from "../../services/helpers";

interface NotificationsPopupProps {
  notificationsInput: ClientNotification[];
  clientID: number;
  onClose: (count: number, notifications: ClientNotification[]) => void;
}

const NotificationsPopup: React.FC<NotificationsPopupProps> = ({
  notificationsInput,
  clientID,
  onClose,
}) => {
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState<
    ClientNotificationType[]
  >([]);
  const [notifications, setNotifications] = useState<ClientNotification[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const loadNotificationTypes = async () => {
      try {
        setLoadingSpinner(true);
        const notificationTypes = await getNotificationTypes();
        setNotificationTypes(notificationTypes);

        setNotifications(notificationsInput);
      } catch (error) {
        setLoadingSpinner(false);
      } finally {
        setLoadingSpinner(false);
      }
    };

    loadNotificationTypes();
  }, [notificationsInput]);

  const handleMarkAsRead = async (notificationID: number) => {
    try {
      setLoadingSpinner(true);
      await markAsRead(notificationID);

      setNotifications((prev) => {
        const updated = prev.filter((n) => n.notificationID !== notificationID);
        return updated;
      });
    } catch {
      console.log("error deleting notification.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setLoadingSpinner(true);
      if (!clientID) return;
      await markAllAsRead(clientID);
      setNotifications([]);
      onClose(0, notifications);
    } catch {
      console.log("error deleting all notifications.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <>
      <div
        className="background-mask"
        onClick={() => onClose(notifications.length, notifications)}
      ></div>
      <div className="notification-drawer">
        <div className="notification-header">
          <h3>Notifications</h3>
          <div style={{ display: "flex", gap: "20px" }}>
            {" "}
            <div className="action-buttons">
              <button className="smbtn btn-red" onClick={handleMarkAllAsRead}>
                Clear All
              </button>
            </div>
            <button
              className="close-button"
              onClick={() => onClose(notifications.length, notifications)}
            >
              <i className="bi bi-x-circle"></i>
            </button>
          </div>
        </div>

        {notifications.length === 0 ? (
          <p className="notification-empty">No notifications at this time.</p>
        ) : (
          <>
            <div>
              {notifications.map((note) => {
                const type = notificationTypes.find(
                  (t) =>
                    t.clientNotificationTypeID ===
                    note.clientNotificationTypeID,
                );

                return (
                  <div
                    key={note.notificationID}
                    className="notification-item"
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(`/client/view-order/${note.orderID}`)
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <div className="notification-type">{type?.typeName}</div>
                      <div>{formatTime(note.createdAt)}</div>
                    </div>
                    <div className="notification-re">
                      RE: {note.subjectName}
                    </div>
                    <div className="notification-default">
                      {type?.defaultMessage}
                    </div>
                    <div>
                      <button
                        className="xsbtn btn-purple"
                        onClick={(e) => {
                          handleMarkAsRead(note.notificationID);
                          e.stopPropagation();
                        }}
                      >
                        Mark as Read
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {loadingSpinner && (
          <div className="loading-mask">
            <div className="loading-spinner"></div>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationsPopup;
